const { getStorage } = require('helpers/general');

const getDate = (oneYearLater = true) => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = d.getDate();
  if (!oneYearLater) {
    return `${year}${month}${day}`;
  }
  return new Date(year + 1, month, day);
};

const stripHtmlTags = html => {
  let plainText = html.replace(/<[^>]*>/g, '');
  plainText = plainText
    .replace(/</g, '<')
    .replace(/>/g, '>')
    .replace(/&/g, '&')
    .replace(/"/g, '"')
    .replace(/'/g, "'")
    .replace(/ /g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&reg;/g, '®')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\\r\\n/g, ' ')
    .replace(/&apos;/g, "'")
    .replace(/\\n/g, '');
  return plainText;
};

const railwayApi = async (url, method = 'GET', body = null) => {
  try {
    const token = getStorage('_loggedIn');
    const loginRequired = 'customer';

    if (!token && url.includes(loginRequired)) {
      return;
    }
    const apiUrl = process.env.GATSBY_BC_API;
    const request = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'authorization-BC': token
      }
    };

    if (body && method !== 'GET') {
      request.body = JSON.stringify(body);
    }
    const response = await fetch(`${apiUrl}/${url}`, request);
    const textResponse = await response.text();

    if (!textResponse) return textResponse;
    const res = JSON.parse(textResponse);
    return { ...res, statusCode: response.status };
  } catch (error) {
    console.error('Error fetching data from Railway API:', error);
    return { success: false, message: 'Failed to fetch data' };
  }
};

const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

const deobfuscateOrderId = (encoded, hash) => {
  if (!encoded || !hash) return;
  const generateChecksum = orderId => {
    return (
      String(orderId)
        .split('')
        .reduce((acc, c) => acc + c.charCodeAt(0), 0) % 1000
    );
  };

  try {
    const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');

    const xored = atob(base64).split(',').map(Number);
    const decoded = xored
      .map((c, i) => String.fromCharCode(c ^ hash.charCodeAt(i % hash.length)))
      .join('');

    const [orderId, checksum] = decoded.split(':');

    const expectedChecksum = generateChecksum(orderId);
    if (Number(checksum) !== expectedChecksum) {
      console.warn('Invalid orderId — checksum mismatch');
      return null;
    }

    return orderId;
  } catch (e) {
    console.error('deobfuscateOrderId failed:', e);
    return null;
  }
};

const calculateDiscountPrice = (price, discount) => {
  const productPrice = Number(price);
  if (!discount || !discount.matchedRule) {
    return {
      discountPrice: '0.00',
      discountPercent: '0.00'
    };
  }
  let discountPrice = 0;
  let discountPercent = 0;
  const amount = Number(discount.matchedRule.amount);

  switch (discount.matchedRule.method) {
    case 'fixed':
      discountPrice = amount;
      discountPercent = (((productPrice - amount) / productPrice) * 100).toFixed(2);
      break;

    case 'percent':
      discountPercent = amount;
      const reduction = productPrice * (amount / 100);
      discountPrice = productPrice - reduction;
      break;

    case 'price':
      discountPrice = productPrice - amount;
      discountPercent = ((amount / productPrice) * 100).toFixed(2);
      break;

    default:
      discountPrice = productPrice;
      discountPercent = 0;
  }

  return {
    discountPrice: Number(discountPrice).toFixed(2),
    discountPercent: Number(discountPercent).toFixed(2)
  };
};

const getCustomerGroupDiscount = (BCCustomerGroups, categories, productId, productPrice) => {
  if (!BCCustomerGroups?.length) return null;

  const haveDiscount = BCCustomerGroups.map(edge => {
    const rules = edge?.node?.discount_rules || edge?.discount_rules;
    const categoryIds = categories?.map(c => c.entityId) || [];

    const productRule = rules.find(
      r => r.type === 'product' && Number(r.product_id) === Number(productId)
    );
    const categoryRule = rules.find(
      r => r.type === 'category' && categoryIds.includes(Number(r.category_id))
    );
    const allRule = rules.find(r => r.type === 'all');

    const matchedRule = productRule || categoryRule || allRule;
    if (!matchedRule) return null;

    return {
      ...edge.node,
      matchedRule: matchedRule
    };
  })
    .filter(Boolean)
    .sort((a, b) => b.bigcommerce_id - a.bigcommerce_id)[0];

  if (haveDiscount) {
    return { ...haveDiscount, ...calculateDiscountPrice(productPrice, haveDiscount) };
  }
  return haveDiscount;
};

const calculateCustomerGroupDiscount = (auth, customerGroups, cart) => {
  if (!auth?.state?.isLoggedIn) {
    return 0;
  }

  const customerGroupId = auth.state.object.customer_group_id;

  if (!customerGroupId || customerGroupId === 0) {
    return 0;
  }

  if (!customerGroups || customerGroups.length === 0) {
    return 0;
  }
  const groupCanDisplay = customerGroups.filter(
    group => group.node.displayDiscountName !== null && group.node.displayDiscountName !== ''
  );
  const matchingGroup = groupCanDisplay.find(
    group => group.node.bigcommerceId === customerGroupId
  )?.node;

  if (!matchingGroup) {
    return 0;
  }

  let totalDiscount = 0;
  const appliedRules = new Set();
  const discountRules = matchingGroup.discountRules || [];
  const cartItems = cart.lineItems?.physical_items || [];

  const calculateItemDiscount = (matchedRule, total, amount) => {
    if (matchedRule.method === 'percent') {
      return ((total || 0) * parseFloat(amount)) / 100;
    } else if (matchedRule.method === 'price') {
      return parseFloat(amount);
    } else if (matchedRule.method === 'fixed') {
      return parseFloat(amount);
    }
    return 0;
  };

  cartItems.forEach(item => {
    let itemDiscount = 0;
    const matchingRule = discountRules.find(rule => {
      if (rule.type === 'product') {
        return rule.productId === String(item.product_id);
      } else if (rule.type === 'category') {
        const productInfo = cart.productInfo?.[item.product_id];
        return (
          productInfo?.categories?.includes(parseInt(rule.categoryId)) ||
          appliedRules.has(`category_${rule.categoryId}`)
        );
      } else if (rule.type === 'all') {
        return !appliedRules.has('all');
      }
      return false;
    });

    if (matchingRule) {
      itemDiscount = calculateItemDiscount(
        matchingRule,
        item.original_price * item.quantity,
        matchingRule.amount
      );
      totalDiscount += itemDiscount;
    }
  });

  return {
    bigcommerceId: matchingGroup.bigcommerceId,
    displayName: matchingGroup.displayName,
    displayDiscountName: matchingGroup.displayDiscountName,
    totalDiscount
  };
};

module.exports = {
  getDate,
  stripHtmlTags,
  delay,
  railwayApi,
  deobfuscateOrderId,
  getCustomerGroupDiscount,
  calculateCustomerGroupDiscount
};
